import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ThemeToggle } from "../components/ThemeToggle";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
    const { isAuthenticated, isLoading, signIn } = useAuth();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    if (!isLoading && isAuthenticated) {
        const dest = location.state?.from || "/";
        return <Navigate to={dest} replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        const res = await signIn({ email: email.trim().toLowerCase(), password });
        setBusy(false);
        if (!res.ok) {
            setError(res.error);
            toast.error(res.error);
        }
    };

    return (
        <div className="pw-grain relative min-h-screen w-full overflow-hidden">
            {/* Industrial background accent */}
            <div
                aria-hidden
                className="pointer-events-none absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-primary/10 blur-3xl"
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full bg-secondary/15 blur-3xl"
            />

            <header className="relative z-10 flex items-center justify-between px-6 py-5 sm:px-10">
                <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-[0_0_0_2px_rgb(var(--background)),0_0_0_3px_rgb(var(--primary))]">
                        <span className="text-sm font-black tracking-tighter">P</span>
                    </div>
                    <div className="leading-tight">
                        <div className="text-sm font-semibold tracking-wide text-foreground">
                            Platzwart
                        </div>
                        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                            Floor Mode // Admin Console
                        </div>
                    </div>
                </div>
                <ThemeToggle />
            </header>

            <main className="relative z-10 mx-auto flex w-full max-w-[440px] flex-col gap-8 px-6 pb-16 pt-6 sm:pt-14">
                <div>
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        <span className="h-1.5 w-1.5 rounded-full bg-success" /> P-0 Skelett
                    </div>
                    <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                        Anmelden
                    </h1>
                    <p className="mt-3 text-sm text-muted-foreground">
                        Zwei Welten, ein Werkzeug. Mobile für die Lagerhalle, Desktop
                        für den Leitstand.
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    className="flex flex-col gap-5 rounded-2xl border border-border bg-card/70 p-6 shadow-xl shadow-black/20 backdrop-blur"
                    data-testid="login-form"
                >
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                            E-Mail
                        </Label>
                        <Input
                            id="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="du@firma.example"
                            className="h-12 bg-input text-base"
                            data-testid="login-email-input"
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                            Passwort
                        </Label>
                        <Input
                            id="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="********"
                            className="h-12 bg-input text-base"
                            data-testid="login-password-input"
                        />
                    </div>

                    {error ? (
                        <div
                            className="rounded-md border border-destructive/40 bg-[rgb(var(--destructive-bg))] px-3 py-2 text-sm text-foreground"
                            data-testid="login-error"
                        >
                            {error}
                        </div>
                    ) : null}

                    <Button
                        type="submit"
                        disabled={busy}
                        className="pw-primary-cta w-full bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="login-submit-btn"
                    >
                        {busy ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <LogIn className="mr-2 h-4 w-4" />
                        )}
                        Anmelden
                    </Button>
                </form>

                {/* #46 Punkt 1: Passwort-Reset ist Admin-Sache (#41/#42) -- das
                    gehört neben den Hinweis zur Kontoanlage, sonst sucht der
                    Nutzer vergeblich nach einem Reset-Link. */}
                <p className="text-center text-xs text-muted-foreground" data-testid="login-help-hint">
                    Zugang fehlt? Dein Admin legt dich im Leitstand an.
                    <br />
                    Passwort vergessen? Dein Admin setzt dir ein neues.
                </p>
            </main>
        </div>
    );
}
